import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarPlus, Users, ListChecks, Activity, CheckCircle2 } from 'lucide-react';
export function FillOpenDatePage() {
  const steps = [{
    title: 'Create the need',
    desc: 'Set date, room, genre, budget, and event type.',
    icon: CalendarPlus
  }, {
    title: 'Review matching artists',
    desc: 'Compare options by fit, availability, and fee band.',
    icon: Users
  }, {
    title: 'Shortlist and reach out',
    desc: 'Invite artists or send structured offers from Booker.',
    icon: ListChecks
  }, {
    title: 'Track responses and holds',
    desc: 'Keep the whole team aligned on what is pending.',
    icon: Activity
  }, {
    title: 'Convert to confirmed booking',
    desc: 'Move straight into contract, deposit, and show logistics.',
    icon: CheckCircle2
  }];
  return <div className="min-h-screen pt-32 pb-24" data-id="element-7200">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 mb-20 text-center" data-id="element-7201">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-textMain mb-6 tracking-tight" data-id="element-7202">
          Got an open date? Booker helps you fill it faster.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted leading-relaxed" data-id="element-7203">
          Create an event need, set your budget and room constraints, discover
          artists that fit, and move quickly from shortlist to offer.
        </motion.p>
      </section>

      {/* Problem Section */}
      <section className="max-w-4xl mx-auto px-6 mb-20" data-id="element-7204">
        <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-8 md:p-12 text-center" data-id="element-7205">
          <h2 className="text-2xl font-bold text-textMain mb-4" data-id="element-7206">
            Empty calendar slots cost time and money.
          </h2>
          <p className="text-textMuted leading-relaxed" data-id="element-7207">
            When a date opens up, teams often scramble through contacts, old
            notes, and ad hoc outreach. Booker turns an empty date into a
            structured search and booking workflow.
          </p>
        </div>
      </section>

      {/* Scenario Story */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7208">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-7209">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" data-id="element-7210"></div>
          <div className="prose prose-invert max-w-none" data-id="element-7211">
            <p className="italic text-lg text-textMuted leading-relaxed mb-6" data-id="element-7212">
              It is Wednesday afternoon and a Saturday slot just opened up. The
              room still needs talent, the budget is not unlimited, and nobody
              wants to waste time emailing artists who are unavailable or wrong
              for the room.
            </p>
            <p className="italic text-lg text-textMuted leading-relaxed" data-id="element-7213">
              Booker turns that open date into a structured event need. The
              platform recommends artists by fit, availability, budget, and room
              type, so the venue team can compare real options and get an offer
              out quickly.
            </p>
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7214">
        <div className="space-y-6" data-id="element-7215">
          {steps.map((step, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-6 bg-surface border border-border rounded-2xl p-6" data-id="element-7216">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0" data-id="element-7217">
                <step.icon className="w-6 h-6 text-primary" data-id="element-7218" />
              </div>
              <div data-id="element-7219">
                <h3 className="text-xl font-bold text-textMain mb-2" data-id="element-7220">
                  Step {i + 1} — {step.title}
                </h3>
                <p className="text-textMuted" data-id="element-7221">{step.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7222">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 text-center" data-id="element-7223">
          <h2 className="text-2xl font-bold text-textMain" data-id="element-7224">
            Fill more dates without programming by memory.
          </h2>
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-7225">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7226">
          <Link to="/demo" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-7227">
            Book a demo
          </Link>
          <Link to="/onboarding" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-7228">
            See Booker in action
          </Link>
        </div>
      </section>
    </div>;
}